import { composeEffects } from '@opengov/compose-app/core/module';
import { createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectUseQueryService } from '@opengov/ogql-client/selectors';
import { IOgqlJobResult, IOgqlV1JobResult } from '@opengov/ogql-types';
import { ogqlDataToChartFormat } from '@opengov/reporting-transforms/visualizations/ogql';
import { EQueryType, EReportColumnTypes, IReportConfiguration, IReportDatasetLens } from '@opengov/reporting-types';
import { result } from 'lodash';

import { IReportLocale } from '../common/locale';
import { runV0Query } from '../common/reportConfiguration/effects/queries/runQueries';
import { selectCurrentLens, selectCurrentTemplateId } from '../common/reportTemplate/standardTemplateSelectors';
import { reportActionCreators } from '../common/report/state';
import { MuiToastTypes } from '../common/helpers/utils';
import { summaryTableActionCreators } from './state';

const defaultLimit = 1000;

export const MAX_SUMMARY_TABLE_ROWS = 21000;
const limitRegex = /limit\s+(\d+)/i;

export const getLimit = (query: string): number => {
  if (limitRegex.test(query)) {
    return parseInt(limitRegex.exec(query)[1], 10);
  }

  return defaultLimit;
};

export const hasMoreResults = (
  results: IOgqlJobResult['jobResult'] | IOgqlV1JobResult['jobResult'],
  limit: number
): boolean => {
  return !!(results && results.pagination && results.pagination.next) || results.data.length === limit;
};

export const summaryTableEffects = composeEffects('report-summary', (compose) => ({
  continueVisualizationQuery: compose.statefulEffect(
    createStructuredSelector({
      useQueryService: selectUseQueryService,
      lens: selectCurrentLens,
      templateId: selectCurrentTemplateId
    }),
    async (
      state: { useQueryService: boolean; lens: IReportDatasetLens; templateId: string },
      e: {
        result: IOgqlJobResult | IOgqlV1JobResult;
        reportConfiguration: IReportConfiguration;
        reportMsgs: IReportLocale;
        isStandardTemplate: boolean;
      }
    ) => {
      const {
        result: { jobResult },
        reportConfiguration,
        reportMsgs: {
          query: { summaryTooMuchData, visualizationTruncated, failure }
        },
        isStandardTemplate
      } = e;
      const { useQueryService, lens, templateId } = state;
      const columns =
        (jobResult as IOgqlJobResult['jobResult']).columns ||
        (jobResult as IOgqlV1JobResult['jobResult']).metadata.columns;

      const isPercentOfTotal =
        reportConfiguration.visualizationQuery.showColumn.columnType === EReportColumnTypes.percentOfTotal;

      const limit = getLimit(jobResult.query);

      if (hasMoreResults(jobResult, limit)) {
        // oh no!  viz data was truncated... better say something
        reportActionCreators.toggleMuiToast({
          type: MuiToastTypes.FAILURE,
          headerText: visualizationTruncated.headerText,
          bodyText: visualizationTruncated.bodyText
        });

        // try to fetch all the data for the summary table
        // we don't keep the first page because lenses have no result order determination at this time
        // and if we concat pages we can get duplicates
        return runV0Query({
          isStandardTemplate,
          reportId: reportConfiguration.reportId,
          lensId: lens && lens.id,
          templateId,
          body: isStandardTemplate ? { reportConfiguration } : reportConfiguration,
          queryType: EQueryType.summaryTableQuery,
          useLargeScaleQueryLimit: false,
          useQueryService,
          limit: MAX_SUMMARY_TABLE_ROWS - limit
        })
          .then((secondResult) => {
            if (hasMoreResults(secondResult.jobResult, getLimit(secondResult.jobResult.query))) {
              reportActionCreators.toggleMuiToast({
                type: MuiToastTypes.FAILURE,
                headerText: summaryTooMuchData.headerText,
                bodyText: summaryTooMuchData.bodyText
              });
            } else {
              // less than the limit rows returned ( we are good )
              const largeSetOfD3Data = ogqlDataToChartFormat(columns, secondResult.jobResult.data, isPercentOfTotal);
              summaryTableActionCreators.setDataAndConfig({
                data: largeSetOfD3Data,
                reportConfiguration,
                columns
              });
              return secondResult;
            }
          })
          .catch((err) => {
            console.error(err);
            reportActionCreators.toggleMuiToast({
              type: MuiToastTypes.FAILURE,
              headerText: failure.headerText,
              bodyText: failure.bodyText
            });
          });
      } else {
        const d3Data = ogqlDataToChartFormat(columns, jobResult.data, isPercentOfTotal);

        summaryTableActionCreators.setDataAndConfig({
          data: d3Data,
          reportConfiguration: e.reportConfiguration,
          columns
        });
        return result;
      }
    }
  )
}));
